/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module UpdateModule
 */

import {
    AfterViewInit,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';
import * as globalL10n from 'global-l10n';
import { UpgradeOperation } from 'generated-types';
import { ITSEGroup } from 'ajs/modules/service-engine-group/factories/se-group.item.factory';
import { Image } from 'ajs/modules/upgrade/factories/image.item.factory';

import {
    IImageSelectionData,
    imageTypeHash,
    UpgradeType,
} from 'ng/modules/update/update.types';

import { UpdateService } from 'ng/modules/update/services/update.service';

import {
    IAviCollectionDataGridConfig,
    IAviCollectionDataGridRow,
} from 'ng/modules/data-grid/components/avi-collection-data-grid/avi-collection-data-grid.types';

import {
    ImageCollection,
    TImageCollection,
} from 'ajs/modules/upgrade/factories/image-collection.factory';

import './seg-image-selection.component.less';
import * as l10n from './seg-image-selection.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description Component for image selection of SEG update page.
 * @author Deepak Chaudhary, Dhanashree Tilak, Sarthak Kapoor
 */
@Component({
    selector: 'seg-image-selection',
    templateUrl: './seg-image-selection.component.html',
})
export class SegImageSelectionComponent implements OnChanges, AfterViewInit {
    @Input()
    public segUpgradeNodeList: ITSEGroup[] = [];

    /**
     * Fire on click of Continue button.
     */
    @Output()
    public onConfirm = new EventEmitter<IImageSelectionData>();

    /**
     * TemplateRef for Image#type column.
     */
    @ViewChild('imageTypeTemplateRef')
    public readonly imageTypeTemplateRef: TemplateRef<HTMLElement>;

    public readonly l10nKeys = l10nKeys;

    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * Available updates grid config.
     */
    public availableUpdatesGridConfig: IAviCollectionDataGridConfig;

    /**
     * List of images selected for upgrade operation.
     */
    public selectedImagesToUpgrade: Image[] = [];

    public imageCollection: ImageCollection;

    /**
     * Enable continue button for valid images selection.
     */
    public isSelectedImagesValidForUpgrade = false;

    /**
     * Store selected images.
     * At most one system image and one patch image can be stored at the same time.
     */
    private selectedImageHash: Record<string, Image | null> = {
        systemImage: null,
        patchImage: null,
    };

    constructor(
        private readonly l10nService: L10nService,
        @Inject(ImageCollection)
        private readonly ImageCollectionFactory: TImageCollection,
        private readonly updateService: UpdateService,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.segUpgradeNodeList && this.segUpgradeNodeList?.length) {
            this.imageCollection = new this.ImageCollectionFactory({
                objectName: 'image-raw-inventory',
                params: {
                    base_image_version: this.getRequestBaseImageVersion(),
                    upgrade_operation: UpgradeOperation.SE_UPGRADE_OP,
                },
            });

            this.initImageCollectionGridConfig();
        }
    }

    /** @override */
    public ngAfterViewInit(): void {
        this.initImageCollectionGridConfig();
    }

    public initImageCollectionGridConfig(): void {
        const { l10nService } = this;

        this.availableUpdatesGridConfig = {
            layout: {
                hideCreate: true,
                hideDelete: true,
                hideEdit: true,
                hideSearch: true,
            },
            id: `${this.imageCollection?.objectName}-seg-list`,
            collection: this.imageCollection,
            fields: [
                {
                    label: l10nService.getMessage(l10nKeys.columnTitleType),
                    id: 'type',
                    templateRef: this.imageTypeTemplateRef,
                },
                {
                    label: l10nService.getMessage(l10nKeys.columnTitleVersion),
                    id: 'version',
                    transform: (row: Image): string => {
                        return row.isUsedByController() ?
                            this.l10nService.getMessage(
                                l10nKeys.currentVersionLabel,
                                [row.version],
                            ) : row.version;
                    },
                },
                {
                    label: l10nService.getMessage(l10nKeys.columnTitleAttributes),
                    id: 'attributes',
                    transform: (row: Image) => row.category,
                },
            ],
            rowSelectionDisabled: (row: Image): boolean => {
                return !this.enableImageCheckbox(row);
            },
        };
    }

    /**
     * Trigger when there is change in image selection from grid.
     */
    public onImageSelectionChange(servers: IAviCollectionDataGridRow[]): void {
        this.selectedImagesToUpgrade = servers as Image[];
        this.isSelectedImagesValidForUpgrade = this.isImageSelectionValid();
        this.updateSelectedImageHash();

        this.onConfirm.emit({
            selectedImagesToUpgrade: this.selectedImagesToUpgrade,
            isSelectedImagesValidForUpgrade: this.isSelectedImagesValidForUpgrade,
        });
    }

    /**
     * Get base image version used to fetch compatible images.
     */
    private getRequestBaseImageVersion(): string {
        return UpdateService.getSegUpgradeListBaseImageVersion(this.segUpgradeNodeList);
    }

    /**
     * Update the selected images hash with selected image list.
     */
    private updateSelectedImageHash(): void {
        this.selectedImageHash = {
            systemImage: null,
            patchImage: null,
        };

        if (!this.isSelectedImagesValidForUpgrade) {
            return;
        }

        this.selectedImagesToUpgrade.forEach(image => {
            if (image.isType(imageTypeHash.IMAGE_TYPE_SYSTEM)) {
                this.selectedImageHash.systemImage = image;
            } else if (image.isType(imageTypeHash.IMAGE_TYPE_PATCH)) {
                this.selectedImageHash.patchImage = image;
            }
        });
    }

    /**
     * Check if image selection is valid for upgrade.
     */
    private isImageSelectionValid(): boolean {
        return this.updateService.isImageListValidForUpgrade(
            this.selectedImagesToUpgrade as any,
            UpgradeType.UPGRADE_TYPE_SEG_UPGRADE,
        );
    }

    /**
     * Enable an image selection checkbox when:
     * 1. No image has been selected yet.
     * 2. It's a selected image.
     * 3. It's a patch image with the same major version as the ONLY selected system image.
     * 4. It's a system image with the same major version as the ONLY selected patch image.
     */
    private enableImageCheckbox(image: Image): boolean {
        const { id } = image;

        const {
            systemImage: selectedSystemImage,
            patchImage: selectedPatchImage,
        } = this.selectedImageHash;

        if (!selectedSystemImage && !selectedPatchImage) {
            return true;
        }

        if (selectedSystemImage && id === selectedSystemImage.id ||
            selectedPatchImage && id === selectedPatchImage.id
        ) {
            return true;
        }

        // Only a system image has been selected.
        if (selectedSystemImage && !selectedPatchImage) {
            if (image.isType(imageTypeHash.IMAGE_TYPE_PATCH) &&
                image.majorVersion === selectedSystemImage.majorVersion
            ) {
                return true;
            }
        }

        // Only a patch image has been selected.
        if (selectedPatchImage && !selectedSystemImage) {
            if (image.isType(imageTypeHash.IMAGE_TYPE_SYSTEM) &&
                image.majorVersion === selectedPatchImage.majorVersion
            ) {
                return true;
            }
        }

        return false;
    }
}
